'use client'
import { useState } from 'react'
import { useFormspree } from '@/hooks/useFormspree'
import { trackEvent } from '@/lib/posthog'
import { POSTHOG_EVENTS } from '@/lib/constants'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface FieldErrors {
  company_name?: string
  contact_email?: string
  mc_number?: string
}

export function EDIForm() {
  const { status, error, submit } = useFormspree(
    process.env.NEXT_PUBLIC_FORMSPREE_EDI_ID ?? ''
  )
  const [fields, setFields] = useState({
    company_name: '',
    contact_email: '',
    mc_number: '',
    edi_type: '204',
    message: '',
  })
  const [errors, setErrors] = useState<FieldErrors>({})

  function set(key: string, value: string) {
    setFields(prev => ({ ...prev, [key]: value }))
  }

  function validate(): boolean {
    const errs: FieldErrors = {}
    if (!fields.company_name.trim()) errs.company_name = 'Company name is required'
    if (!EMAIL_RE.test(fields.contact_email)) errs.contact_email = 'Enter a valid email address'
    if (!fields.mc_number.trim()) errs.mc_number = 'MC number is required'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    submit({ ...fields })
    trackEvent(POSTHOG_EVENTS.EDI_CONTACT_CLICK, { edi_type: fields.edi_type })
  }

  if (status === 'success') {
    return (
      <div className="glass-card rounded-2xl p-10 text-center">
        <p className="text-white text-xl font-semibold">EDI inquiry received!</p>
        <p className="text-[color:var(--foreground-muted)] mt-2 text-sm">
          Our team will contact you with EDI setup details within 1 business day.
        </p>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      aria-label="EDI connection form"
      noValidate
      className="glass-card rounded-2xl p-8 flex flex-col gap-5"
    >
      <div aria-live="polite" aria-atomic="true" className="min-h-[1.25rem]">
        {error && <p className="text-red-400 text-sm font-medium">{error}</p>}
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="edi-company_name" className="text-sm font-medium text-[color:var(--foreground-muted)]">
          Company Name <span aria-hidden="true">*</span>
        </label>
        <input
          id="edi-company_name"
          type="text"
          className={`glass-input rounded-lg px-4 py-3 text-white${errors.company_name ? ' border border-red-500' : ''}`}
          value={fields.company_name}
          onChange={e => set('company_name', e.target.value)}
          autoComplete="organization"
        />
        {errors.company_name && <p className="text-red-400 text-xs">{errors.company_name}</p>}
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="edi-contact_email" className="text-sm font-medium text-[color:var(--foreground-muted)]">
          Contact Email <span aria-hidden="true">*</span>
        </label>
        <input
          id="edi-contact_email"
          type="email"
          className={`glass-input rounded-lg px-4 py-3 text-white${errors.contact_email ? ' border border-red-500' : ''}`}
          value={fields.contact_email}
          onChange={e => set('contact_email', e.target.value)}
          autoComplete="email"
        />
        {errors.contact_email && <p className="text-red-400 text-xs">{errors.contact_email}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label htmlFor="edi-mc_number" className="text-sm font-medium text-[color:var(--foreground-muted)]">
            MC Number <span aria-hidden="true">*</span>
          </label>
          <input
            id="edi-mc_number"
            type="text"
            className={`glass-input rounded-lg px-4 py-3 text-white${errors.mc_number ? ' border border-red-500' : ''}`}
            value={fields.mc_number}
            onChange={e => set('mc_number', e.target.value)}
            placeholder="e.g. MC123456"
          />
          {errors.mc_number && <p className="text-red-400 text-xs">MC number is required</p>}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="edi-edi_type" className="text-sm font-medium text-[color:var(--foreground-muted)]">
            EDI Transaction Set
          </label>
          <select
            id="edi-edi_type"
            className="glass-input rounded-lg px-4 py-3 text-white"
            value={fields.edi_type}
            onChange={e => set('edi_type', e.target.value)}
          >
            <option value="204">204 — Motor Carrier Load Tender</option>
            <option value="214">214 — Shipment Status Message</option>
            <option value="990">990 — Response to Load Tender</option>
            <option value="997">997 — Functional Acknowledgment</option>
          </select>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="edi-message" className="text-sm font-medium text-[color:var(--foreground-muted)]">
          Message <span className="text-xs opacity-60">(optional)</span>
        </label>
        <textarea
          id="edi-message"
          rows={4}
          className="glass-input rounded-lg px-4 py-3 text-white resize-none"
          value={fields.message}
          onChange={e => set('message', e.target.value)}
          placeholder="Additional requirements, volume details, or questions…"
        />
      </div>

      <button
        type="submit"
        disabled={status === 'loading'}
        className="glass-button rounded-xl px-8 py-4 font-semibold text-base mt-2 disabled:opacity-50"
      >
        {status === 'loading' ? 'Sending…' : 'Connect via EDI'}
      </button>
    </form>
  )
}
